// src/oura/overview/useHubStage.ts
//
// Reads what `hubStage()` needs beyond the business row: the onboarding record
// and the open draft of the business's 50, plus a scan running for its site.
// One read per business page, shared through HubContext, so the header, the
// Overview Next card and the empty states all say the same thing.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getOnboarding, getOnboardingPlanFacts, getOnboardingPrefill, isOnboardingLive, type Onboarding } from "@service/aeo/onboarding";
import { isOuraLiveApiEnabled } from "@service/aeo";
import { showOnboarding } from "../nav";
import { findOpenScanDraft } from "@service/aeo/scanDraft";
import type { ScanDraft } from "@service/aeo/scanDraftTypes";
import { useScanJobs } from "../aeo";
import { siteKey } from "../newscan/domain";
import { useScanningSites } from "../newscan/useScanningSites";
import { openGates } from "../components/newscan/openDecisions";
import { useVisiblePoll } from "../lib/useVisiblePoll";
import { draftPurpose } from "@service/aeo/scanDraftTypes";
import { headerPills, hubNext, hubStage, isOpenDraft, type HubAct, type HubDraft, type HubFacts, type HubNext, type HubPill, type HubStage } from "./hubStage";
import type { OnboardingPlanFacts } from "../onboarding/derive";

/**
 * A client can be signed here: onboarding is on and its routes can create the
 * record (live, or the whole of Oura on sample data). Off, no screen offers
 * Client signed, so the press never opens a sheet whose save cannot land.
 */
export const canSignClients: boolean = showOnboarding && (isOnboardingLive || !isOuraLiveApiEnabled);

/** A full draft as the stage reads it, with its open cards counted. */
function hubDraft(d: ScanDraft): HubDraft {
    return {
        id: d.id,
        kind: d.kind,
        purpose: d.purpose ?? null,
        status: d.status,
        createdAt: d.createdAt || null,
        open: d.status === "ready" ? openGates(d).length : null,
    };
}

/** The percent of a scan job running for this site, from the job store. */
function useRunningPercent(domain: string): { percent: number | null } | null {
    const jobs = useScanJobs();
    return useMemo(() => {
        const key = siteKey(domain);
        const job = jobs.find((j) => (j.status === "queued" || j.status === "running") && siteKey(j.domain || "") === key);
        if (!job) return null;
        return { percent: job.stages_total > 0 ? Math.round((job.stages_complete / job.stages_total) * 100) : null };
    }, [jobs, domain]);
}

export interface HubState {
    stage: HubStage | null;
    next: HubNext | null;
    /** The header's stage pills. Empty while the stage is read. */
    pills: HubPill[];
    /** The open draft of the business's own 50 (not a first check), as the stage read it. */
    draft: HubDraft | null;
    /** A first_check draft is open for this business (reviewed from onboarding). */
    firstCheckOpen: boolean;
    /** The onboarding record. Undefined while it is read. */
    onboarding: Onboarding | null | undefined;
    /**
     * The onboarding record, its plan or the open draft could not be read. The stage stays null
     * (no actionable journey controls) until a read succeeds; `refresh` retries.
     */
    failed: boolean;
    refresh(): void;
}

/**
 * The business's stage. `enabled` false (New scan switch off, or not staff):
 * nothing is read and the stage is null, so every screen stays as today.
 */
export function useHubStage(facts: HubFacts, { enabled, onboardingOn, draftFlow }: { enabled: boolean; onboardingOn: boolean; draftFlow: boolean }): HubState {
    const [onboarding, setOnboarding] = useState<Onboarding | null | undefined>(undefined);
    // Any open draft for the business, first checks included; the stage takes its own from it.
    const [draft, setDraft] = useState<HubDraft | null>(null);
    const [plan, setPlan] = useState<OnboardingPlanFacts | null>(null);
    /**
     * Which business each required read has SUCCEEDED for (Jack, #4284, #4288). A stage is built
     * only when every read belongs to the business on screen, checked during render, so a
     * reused page never shows the last business's record, draft or plan for one commit; and a
     * read still on its way is never "no record", "no draft" or "no plan". The open-draft read is
     * required with the New scan flow on: until it answers the card never offers "Draft the 50"
     * or "Scan X" over a draft being read.
     */
    const [onboardingFor, setOnboardingFor] = useState<string | null>(null);
    const [draftFor, setDraftFor] = useState<string | null>(null);
    const [planFor, setPlanFor] = useState<string | null>(null);
    // Separate failures, so one read that succeeds cannot clear another's failure.
    const [onboardingFailed, setOnboardingFailed] = useState(false);
    const [draftFailed, setDraftFailed] = useState(false);
    const [planFailed, setPlanFailed] = useState(false);
    /** Whether the pitch was drafted in Oura: the same read the Client signed sheet makes. Null: not known. */
    const [pitchInOura, setPitchInOura] = useState<boolean | null>(null);
    const seq = useRef(0);
    const { businessId, domain } = facts;

    const refresh = useCallback(() => {
        if (!enabled) return;
        const mine = ++seq.current;
        if (onboardingOn) {
            getOnboarding(businessId).then(
                (o) => {
                    if (seq.current !== mine) return;
                    setOnboarding(o);
                    setOnboardingFor(businessId);
                    setOnboardingFailed(false);
                    // A prospect's card says whether its pitched 50 carry, by the rule the first check's Review uses.
                    if (!o && domain) {
                        void getOnboardingPrefill(domain).then((p) => { if (seq.current === mine) setPitchInOura(!!p.buyMode); }, () => undefined);
                    }
                    // Signature and publish, read as the Onboarding page reads them, once the first check is done.
                    if (o?.scan.status === "complete") {
                        void getOnboardingPlanFacts(businessId).then(
                            (p) => {
                                if (seq.current !== mine) return;
                                setPlan(p);
                                setPlanFor(businessId);
                                setPlanFailed(false);
                            },
                            () => { if (seq.current === mine) setPlanFailed(true); },
                        );
                    } else {
                        // No plan facts are needed before the first check completes.
                        setPlan(null);
                        setPlanFor(businessId);
                        setPlanFailed(false);
                    }
                },
                () => { if (seq.current === mine) setOnboardingFailed(true); },
            );
        } else {
            setOnboarding(null);
            setOnboardingFor(businessId);
            setOnboardingFailed(false);
            setPlan(null);
            setPlanFor(businessId);
            setPlanFailed(false);
        }
        if (draftFlow) {
            void findOpenScanDraft({ businessId }).then(
                (res) => {
                    if (seq.current !== mine) return;
                    if (!res.ok) {
                        setDraftFailed(true);
                        return;
                    }
                    setDraft(res.data ? hubDraft(res.data) : null);
                    setDraftFor(businessId);
                    setDraftFailed(false);
                },
                () => { if (seq.current === mine) setDraftFailed(true); },
            );
        } else {
            setDraft(null);
            setDraftFor(businessId);
            setDraftFailed(false);
        }
    }, [enabled, onboardingOn, draftFlow, businessId, domain]);

    useEffect(() => {
        // Only what has no "for": the reads above are checked against the business during render.
        setPitchInOura(null);
        setOnboardingFailed(false);
        setDraftFailed(false);
        setPlanFailed(false);
        return () => { seq.current += 1; };
    }, [businessId]);

    // A running check or a building draft moves by itself; anything else moves when someone acts.
    const moving = onboarding?.scan.status === "running" || draft?.status === "building";
    useVisiblePoll(refresh, moving ? 3_000 : 60_000, [refresh, moving]);

    const job = useRunningPercent(facts.domain);
    const scanningLabel = useScanningSites().labelFor(facts.domain, businessId);
    const run = job ? { status: "running" as const, percent: job.percent } : scanningLabel ? { status: "running" as const, percent: null } : facts.run;

    // Nothing known to stand on: no stage, rather than one built from a failed read or from a
    // plan still being read. `failed` says which of the two the screen shows (a retry or loading).
    const failed = onboardingFailed || draftFailed || planFailed;
    const mineOnboarding = onboardingFor === businessId ? onboarding : undefined;
    const blocked =
        failed ||
        onboardingFor !== businessId ||
        (draftFlow && draftFor !== businessId) ||
        (mineOnboarding?.scan.status === "complete" && planFor !== businessId);
    const stage = useMemo(
        () => (enabled && mineOnboarding !== undefined && !blocked && !facts.unread ? hubStage({ ...facts, run, pitchInOura }, mineOnboarding, draft, plan) : null),
        // `facts` is rebuilt by the caller on every render; its parts are the deps.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [enabled, mineOnboarding, blocked, draft, plan, pitchInOura, run?.status, run && "percent" in run ? run.percent : null, run && "message" in run ? run.message : null,
            facts.businessId, facts.name, facts.state, facts.engagementStatus, facts.timelinePublished, facts.scanned, facts.scannedAt, facts.nextScanAt, facts.unread],
    );
    const next = useMemo(() => (stage ? hubNext(stage, facts.name, { draftFlow, signOn: canSignClients }) : null), [stage, facts.name, draftFlow]);
    const pills = useMemo(() => (stage ? headerPills(stage, { signOn: canSignClients }) : []), [stage]);
    // The draft of THIS business only: a reused page never shows the last one's.
    const myDraft = draftFor === businessId ? draft : null;
    const firstCheckOpen = !!myDraft && draftPurpose(myDraft) === "first_check" && isOpenDraft(myDraft);
    const own = myDraft && !firstCheckOpen && isOpenDraft(myDraft) ? myDraft : null;
    // Any failed read holds the stage and offers the retry.
    return { stage, next, pills, draft: own, firstCheckOpen, onboarding: mineOnboarding, failed, refresh };
}

/* ---- shared with the screens inside the business page ---- */

export interface HubContextValue extends HubState {
    /** Carries out a press (navigate, or open the Client signed or Rescan sheet). */
    act(a: HubAct): void;
    /** A press that writes here (the signature) is on its way. */
    busy: boolean;
}

export const HubContext = createContext<HubContextValue | null>(null);

/** The business page's stage, or null outside it or with the New scan switch off. */
export function useHub(): HubContextValue | null {
    return useContext(HubContext);
}
